import { useMemo } from 'react';
import { StanTag } from '@/components';
import { StanColumnDef } from '@/components/core-components/StanTable/StanTable.types';
import { AffiliateReseller } from '../types/affiliateAnalytics.types';
import { cn, formatCurrency, formatNumber, STAN_TAG_PRESETS } from '@/lib';
import { useUser } from '@/queries';
import { StanDropdownMenu } from '@/components/core-components/StanDropdown/Menu/StanDropdownMenu';
import { DropdownMenuOption } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { LuEllipsisVertical } from 'react-icons/lu';

const ROW_DROPDOWN_OPTIONS: DropdownMenuOption[] = [
    { label: 'Revoke Access', value: 'revoke', className: 'text-danger' },
    { label: 'Resend Affiliate Link', value: 'resend-link' },
    { label: 'Edit Affiliate Commission', value: 'edit-affiliate-commission' },
];

interface UseAffiliateTableColumnsProps {
    onRevokeAccess: (reseller: AffiliateReseller) => void;
    onResendLink: (reseller: AffiliateReseller) => void;
    onEditCommission: (reseller: AffiliateReseller) => void;
}

export function useAffiliateTableColumns({
    onRevokeAccess,
    onResendLink,
    onEditCommission,
}: UseAffiliateTableColumnsProps) {
    const { user } = useUser();

    const columns: StanColumnDef<AffiliateReseller>[] = useMemo(
        () => [
            {
                header: 'Affiliate',
                accessorKey: 'username',
                width: '30%',
                cell: ({ row }) => {
                    const reseller = row.original;
                    return (
                        <div className='flex items-center gap-3'>
                            {/*TODO: If profile image = https://assets.staging.stanwith.me/beta/user_profile/no_user.png, show default image*/}
                            <img
                                src={reseller.profile_image}
                                alt='User Avatar'
                                className='hidden size-10 rounded-full md:block'
                            />
                            <div className='flex flex-col gap-1'>
                                <span className='font-semibold'>
                                    @{reseller.username} | {reseller.full_name}
                                </span>
                                <span className='text-gray font-semibold'>{reseller.email}</span>
                            </div>
                        </div>
                    );
                },
            },
            {
                header: 'Status',
                accessorKey: 'resell_status',
                align: 'right',
                width: '10%',
                cell: ({ row }) => {
                    const status = row.original.resell_status;
                    const preset = STAN_TAG_PRESETS.affiliateSellerStatus?.[status];

                    // Only show tag if there's a valid preset with a label
                    if (!preset?.label) {
                        return null;
                    }

                    return (
                        <div className='flex justify-end'>
                            <StanTag
                                label={preset.label}
                                style={{
                                    backgroundColor: preset.backgroundColor,
                                    color: preset.textColor,
                                }}
                            />
                        </div>
                    );
                },
            },
            {
                header: 'Purchases',
                accessorKey: 'total_sales',
                align: 'right',
                width: '10%',
                cellClassName: 'font-semibold',
                hideOnMobile: true,
                cell: ({ row }) => formatNumber(row.original.total_sales, { decimals: 0 }),
            },
            {
                header: 'Chargebacks',
                accessorKey: 'total_refunded_or_disputed',
                align: 'right',
                cellClassName: 'font-semibold',
                width: '10%',
                hideOnMobile: true,
                cell: ({ row }) => row.original.total_refunded_or_disputed,
            },
            {
                header: 'Revenue',
                accessorKey: 'total_earnings',
                align: 'right',
                width: '13%',
                cellClassName: 'font-semibold',
                cell: ({ row }) => {
                    const { total_earnings } = row.original;
                    return (
                        <span className={cn(total_earnings > 0 ? 'text-success-main' : 'text-gray', 'font-semibold')}>
                            {total_earnings > 0 ? formatCurrency(total_earnings, user?.data.currency) : '-'}
                        </span>
                    );
                },
            },
            {
                header: '',
                accessorKey: 'actions',
                align: 'right',
                width: '15%',
                hideOnMobile: true,
                cell: ({ row }) => {
                    const reseller = row.original;
                    const status = reseller.resell_status;
                    if (status === 'deleted') {
                        return null;
                    }

                    // Routes dropdown menu selections to appropriate action handlers
                    // Each option triggers a different modal or action based on the selected value
                    const handleDropdownSelect = (option: DropdownMenuOption) => {
                        if (option.value === 'revoke') {
                            onRevokeAccess(reseller);
                        } else if (option.value === 'resend-link') {
                            onResendLink(reseller);
                        } else if (option.value === 'edit-affiliate-commission') {
                            onEditCommission(reseller);
                        }
                    };

                    return (
                        <div className='flex w-full items-center justify-end gap-2'>
                            {reseller.affiliate_custom_percentage && (
                                <div onClick={() => onEditCommission(reseller)} className='cursor-pointer'>
                                    <StanTag
                                        label={`Comm.: ${reseller.affiliate_custom_percentage}%`}
                                        style={{
                                            backgroundColor: 'var(--color-brand-subtle)',
                                            color: 'var(--color-brand-main)',
                                            fontWeight: 600,
                                        }}
                                    />
                                </div>
                            )}
                            <DropdownProvider dropdownId={`affiliate-dropdown-${reseller.id}`}>
                                <StanDropdownMenu
                                    id={`affiliate-dropdown-${reseller.id}`}
                                    name={`affiliate-dropdown-${reseller.id}`}
                                    options={ROW_DROPDOWN_OPTIONS}
                                    onSelect={(_, option) => handleDropdownSelect(option)}
                                    placement='bottom-end'
                                    className='!flex-none'
                                    minWidth={50}
                                    rounded
                                >
                                    <LuEllipsisVertical className='text-gray size-6' />
                                </StanDropdownMenu>
                            </DropdownProvider>
                        </div>
                    );
                },
            },
        ],
        [user?.data.currency, onRevokeAccess, onResendLink, onEditCommission]
    );

    return columns;
}
